import { useCallback, useEffect, useRef, useState } from "react";

/**
 * One GET for the fund pages — `call()` is a service call like
 * `investingApi.scoreboard.get` — as `{loading, data, error, retry}`. A 404
 * is `data: null`, not an error: before the first snapshot the scoreboard
 * answers 404, and that's the empty state. Reads again when `reloadTick`
 * moves or on `retry()`; only the newest read lands. `call` must be
 * stable (a service method, not an inline function).
 */
export default function useRead(call, reloadTick = 0) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [retryTick, setRetryTick] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    const request = ++requestRef.current;
    setState((now) => ({ ...now, loading: true, error: null }));
    call().then(
      ({ data }) => {
        if (request !== requestRef.current) return;
        setState({ loading: false, data, error: null });
      },
      (err) => {
        if (request !== requestRef.current) return;
        if (err?.response?.status === 404) setState({ loading: false, data: null, error: null });
        else setState({ loading: false, data: null, error: err });
      },
    );
  }, [call, reloadTick, retryTick]);

  const retry = useCallback(() => setRetryTick((tick) => tick + 1), []);
  return { ...state, retry };
}
